/**
 * useStamina - スタミナ残量・UI タイマー・広告回復を扱う React フック。
 *
 * - 1 秒ごとに `computeCurrentStamina()` を再計算して UI を更新する（pure 関数なので
 *   storage 書き込みは発生しない）。
 * - `consume()` / `recoverFullViaAd()` / `claimDailyBonusViaAd()` は呼び出し時に
 *   `persistCurrentStamina()` 経由で storage も更新される。
 * - 広告スタブ経由の回復は `services/adsStub.ts` の `showRewardedAdStub()` を使用。
 *   将来の SDK 差し替えに備えて UI からは「回復する」ボタン 1 つで完結する形で提供。
 *
 * 参考ルール: `.claude/rules/no-silent-fallback.md`
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  canClaimDailyBonus as canClaimDailyBonusRaw,
  claimDailyBonus as claimDailyBonusRaw,
  computeCurrentStamina,
  consumeStamina as consumeStaminaRaw,
  recoverFull,
  type ConsumeMode,
} from '../services/stamina'
import { showRewardedAdStub } from '../services/ads'

interface UseStaminaReturn {
  /** 現在のスタミナ残量。 */
  stamina: number
  /** 現在適用中の最大スタミナ（ボーナス込み）。 */
  maxStamina: number
  /** 次回自然回復までの残り時間 "m:ss" 形式。満タン時は空文字。 */
  nextRecoveryLabel: string
  /** ゲームを開始できる状態か（stamina >= 1）。 */
  canPlay: boolean
  /** 広告スタブを表示中か（重複再生防止用）。 */
  isRecovering: boolean
  /** ボーナススタミナ用の広告を表示中か（重複再生防止用）。 */
  isClaimingDailyBonus: boolean
  /** 今日のデイリーボーナスがまだ受け取れるか。 */
  canClaimDailyBonus: boolean
  /** 1 消費する。失敗（残 0 等）時は false。 */
  consume: (mode?: ConsumeMode) => boolean
  /** 広告スタブ → 成功時にフル回復。戻り値は回復に成功したか。 */
  recoverFullViaAd: () => Promise<boolean>
  /** リワード広告 → 成功時にデイリーボーナスを受け取る。 */
  claimDailyBonusViaAd: () => Promise<boolean>
}

/**
 * 残ミリ秒を "m:ss" にフォーマット。
 *
 * 0 以下の場合は空文字を返し、UI 側で「次回回復待ち」表示を出さない。
 */
function formatRemainingMs(ms: number): string {
  if (ms <= 0) return ''
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/** UI 更新の間隔（ミリ秒）。残り時間の "m:ss" を 1 秒刻みで更新する目安。 */
const STAMINA_UI_TICK_MS = 1000

export function useStamina(): UseStaminaReturn {
  const initial = computeCurrentStamina()

  const [stamina, setStamina] = useState<number>(initial.amount)
  const [maxStamina, setMaxStamina] = useState<number>(initial.maxStamina)
  const [nextRecoveryLabel, setNextRecoveryLabel] = useState<string>(
    formatRemainingMs(initial.nextRecoveryInMs),
  )
  const [canClaim, setCanClaim] = useState<boolean>(canClaimDailyBonusRaw())
  const [isRecovering, setIsRecovering] = useState<boolean>(false)
  const [isClaimingDailyBonus, setIsClaimingDailyBonus] = useState<boolean>(false)

  const intervalRef = useRef<number | null>(null)

  // 最新状態を UI に反映するヘルパ（storage 書き込みなし）。
  const syncFromState = useCallback(() => {
    const current = computeCurrentStamina()
    setStamina(current.amount)
    setMaxStamina(current.maxStamina)
    setNextRecoveryLabel(formatRemainingMs(current.nextRecoveryInMs))
    setCanClaim(canClaimDailyBonusRaw())
  }, [])

  // 1 秒タイマーで残時間とスタミナ値を更新。
  useEffect(() => {
    intervalRef.current = window.setInterval(syncFromState, STAMINA_UI_TICK_MS)
    return () => {
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [syncFromState])

  const consume = useCallback(
    (mode?: ConsumeMode): boolean => {
      const ok = consumeStaminaRaw({ mode })
      if (ok) syncFromState()
      return ok
    },
    [syncFromState],
  )

  const recoverFullViaAd = useCallback(async (): Promise<boolean> => {
    if (isRecovering) return false
    setIsRecovering(true)
    try {
      const watched = await showRewardedAdStub('stamina')
      if (!watched) return false
      recoverFull()
      syncFromState()
      return true
    } finally {
      setIsRecovering(false)
    }
  }, [isRecovering, syncFromState])

  const claimDailyBonusViaAd = useCallback(async (): Promise<boolean> => {
    if (isClaimingDailyBonus) return false
    if (!canClaimDailyBonusRaw()) {
      syncFromState()
      return false
    }

    setIsClaimingDailyBonus(true)
    try {
      const watched = await showRewardedAdStub('dailyBonus')
      if (!watched) return false
      const ok = claimDailyBonusRaw()
      if (ok) syncFromState()
      return ok
    } finally {
      setIsClaimingDailyBonus(false)
    }
  }, [isClaimingDailyBonus, syncFromState])

  return {
    stamina,
    maxStamina,
    nextRecoveryLabel,
    canPlay: stamina >= 1,
    isRecovering,
    isClaimingDailyBonus,
    canClaimDailyBonus: canClaim,
    consume,
    recoverFullViaAd,
    claimDailyBonusViaAd,
  }
}
